import AppContext from "@lightspeed-invoicing/core-client/frontend/app/Context"
import React, { useState } from "react"
import { Form, OverlayTrigger } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { ConnectedProps, connect } from "react-redux"
import { Settings, SupportedSettings } from "../../../settings/account/Settings"
import PricingRow from "../../../state/pricing/PricingRow"
import { TOOGGLE_DESCRIPTION_TYPE } from "../../../state/pricing/ProductDescription"
import { patchPricingTableItem } from "../../../state/pricing/actions"
import { getPricingTableItem } from "../../../state/pricing/selectors"
import PreferenceService from "../../../state/user/PreferenceService"
import EventBus from "../../EventBus.js"
import Icon from "../../icon/icon.component"
import RichTextEditor from "../../rich-text/editor.component.jsx"

type OwnProps = {
    itemId: string
    mainObjectId: string
    productDescription: TOOGGLE_DESCRIPTION_TYPE
    editable: boolean
    type: string
    popupClassName?: string
}



const ItemLineDescription = (props: Props) => {
    const pricingRow: PricingRow | undefined = props.item ? new PricingRow(props.item) : undefined
    const [title, setTitle] = useState<string>(pricingRow?.getActiveTitle() || "")
    const [shortDescription, setShortDescription] = useState<string>(pricingRow?.getActiveShortDescription() || "")
    const [longDescription, setLongDescription] = useState<string>(pricingRow?.getActiveLongDescription() || "")
    const [productDescription, setProductDescription] =
        useState<TOOGGLE_DESCRIPTION_TYPE | string>(props.productDescription ?? "")
    const [editMode, setEditMode] = useState<boolean>(false)


    const toggleDescription = (descriptionType: TOOGGLE_DESCRIPTION_TYPE) => {
        return function () {
            setProductDescription(descriptionType)
            PreferenceService.UpdateUserPreference({
                productDescription: descriptionType
            }, AppContext.getUserLoggedIn().id)
        }
    }

    const save = (event: React.MouseEvent) => {
        event.preventDefault()
        props.dispatch(patchPricingTableItem(props.item?.id ?? "", props.mainObjectId, props.type, {
            title: title,
            shortDescription: shortDescription,
            longDescription: longDescription,
            mainObjectId: props.mainObjectId,
        }, false, false))
        setEditMode(false)
    }

    const triggerEdit = (value: boolean) => {
        if (value) {
            EventBus.dispatch(EventBus.TopicInlineDescriptionEdit, {
                type: props.type,
            })
        }
        setEditMode(value)
    }

    const initializeTitle = () => {
        const titleInitial = pricingRow?.getActiveTitle() || ""
        const shortDescriptionInitial = pricingRow?.getActiveShortDescription() || ""
        if (Settings.get(SupportedSettings.ShowShortDescriptionInListing) && shortDescriptionInitial != "") {
            return (`${titleInitial} - ${shortDescriptionInitial}`)
        }
        return titleInitial
    }

    const ProductTitleLabel = () => {
        return (<>{initializeTitle()}</>)
    }

    const DescriptionLabel = () => {
        return (<span>
            <a  className={"p-0"} onClick={toggleDescription(TOOGGLE_DESCRIPTION_TYPE.SHORT_DESCRIPTION)}>
                <Icon value={productDescription === TOOGGLE_DESCRIPTION_TYPE.SHORT_DESCRIPTION ? "caret-square-right" : "square"}
                    className={"mr-1"} />
                <FormattedMessage
                    id="product.form.edit.short_description_label"
                    defaultMessage="Short Description"
                />
            </a>
            &nbsp;|&nbsp;
            <a  className={"p-0"} onClick={toggleDescription(TOOGGLE_DESCRIPTION_TYPE.LONG_DESCRIPTION)}>
                <Icon value={productDescription === TOOGGLE_DESCRIPTION_TYPE.LONG_DESCRIPTION ? "caret-square-right" : "square"}
                    className={"mr-1"} />
                <FormattedMessage
                    id="product.form.edit.long_description_label"
                    defaultMessage="Long Description"
                />
            </a>
        </span>)
    }

    const DropDownMenu = <div className={props.popupClassName + " dropdown-menu p-3 w-100 "} style={{ maxWidth: "400px" }}>
        <div className="w-100 m-0">
            <div className="mb-1 row">
                <label className="col-12 col-form-label custom-form-label strong" htmlFor="productTitle">
                    <FormattedMessage
                        id="product.form.edit.title_label"
                        defaultMessage="Title"
                    />
                </label>
                <div className="col-12">
                    <Form.Control name="title" value={title} onChange={e => { setTitle(e.target.value) }}
                        id="productTitle" type="text" className="form-control" />
                </div>
            </div>
            {productDescription === TOOGGLE_DESCRIPTION_TYPE.SHORT_DESCRIPTION ? <div className="mb-1 row">
                <label className="col-12 col-form-label custom-form-label strong"
                    htmlFor="productShortDescription">
                    <DescriptionLabel />
                </label>
                <div className="col-12">
                    <textarea name="shortDescription"
                        value={shortDescription}
                        onChange={e => {
                            setShortDescription(e.target.value)
                        }}
                        id={"productShortDescription"}
                        rows={3}
                        className="form-control"
                    />
                </div>
            </div> : <div className={"mb-1 row"}>
                <label className="col-12 col-form-label custom-form-label strong"
                    htmlFor="productLongDescription">
                    <DescriptionLabel />
                </label>
                <div className="col-12">
                    <RichTextEditor
                        enableMergeFields={false}
                        simple={true}
                        height={200}
                        name="longDescription"
                        value={longDescription}
                        onChange={value => {
                            setLongDescription(value)
                        }}
                        id="productLongDescription"
                        className="form-control"
                    />
                </div>
            </div>}
        </div>
        <p className="m-0 pt-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => triggerEdit(false)}>
                <Icon value={"arrow-left"} />
            </button>
            <button className="btn btn-sm btn-outline-primary ml-2" onClick={save}>
                <Icon value={"save"} />
            </button>
        </p>
    </div>

    if (!props.editable) {
        return <div>
            {title}
        </div >
    }

    return (
        <>
            <OverlayTrigger show={editMode} onToggle={triggerEdit} trigger={["click"]}
                placement="bottom" overlay={DropDownMenu} rootClose={true}>
                <a href={"#"} onClick={(event) => event.preventDefault()} className={"text-truncate"}>
                    <div className={"text-wrap text-break"}>
                        <ProductTitleLabel />
                        <Icon className="pl-1" value="pen" />
                    </div>
                </a>
            </OverlayTrigger>
        </>
    )
}

const mapState = (state, props: OwnProps) => {
    const id = props.itemId
    const objectId = props.mainObjectId
    const item = getPricingTableItem(state, id, objectId)
    return {
        ...props,
        item: item,
    }
}
const connector = connect(mapState)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(ItemLineDescription))


// WEBPACK FOOTER //
// ./src/common/quote/component/itemLineDescription.tsx